/**
 * useIncomingCallAlert — входящий звонок слышно и видно, даже когда CRM не на
 * экране (DEV-2568). Раньше баннер звонка всплывал беззвучно, и при вкладке в фоне
 * или свёрнутом окне звонок не замечали.
 *
 * - Мелодия, пока звонок ждёт ответа. Звонит одна вкладка из открытых (Web Locks),
 *   иначе несколько вкладок CRM звенели бы вразнобой.
 * - Системное уведомление браузера, если окно CRM не в фокусе; клик — на вкладку.
 * - Трубка и имя звонящего в заголовке вкладки.
 */
import { onBeforeUnmount, ref, watch, type Ref } from 'vue'
import { useWebPush } from '@/features/notifications/composables/useWebPush'

/**
 * Тот же tag, что у web push о звонке (notification_type в notifications-service):
 * по нему уведомления закрываются, когда звонок взяли или он кончился.
 */
export const CALL_NOTIFICATION_TAG = 'incoming_call'

const RING_LOCK = 'vinprop-incoming-call-ring'
// Двойной звонок раз в 3 с, как у телефона; расписания хватает на весь звонок (75 с).
const RING_PERIOD_S = 3
const RING_BURSTS: readonly (readonly [number, number])[] = [
  [0, 0.4],
  [0.6, 1],
]
const RING_TOTAL_S = 75
const RING_GAIN = 0.12
// resume() без жеста пользователя в Chrome не отвечает вовсе. С жестом он ждёт
// аудиовыход — гарнитуре Bluetooth нужна до секунды и больше.
const RESUME_WAIT_MS = 2000
const RESUME_WAIT_NO_GESTURE_MS = 300
// Замок отпускаем не сразу: соседние вкладки за это время получат taken / ENDED и
// не пискнут обрывком мелодии по уже взятому звонку.
const RELEASE_DELAY_MS = 1500

export interface IncomingCallAlert {
  /** Звонок: сменился — мелодия начинается заново. */
  id: string
  title: string
  body: string
}

const notificationsSupported = () => typeof window !== 'undefined' && 'Notification' in window

export function useIncomingCallAlert(alert: Ref<IncomingCallAlert | null>) {
  let audio: AudioContext | null = null
  let releaseLock: (() => void) | null = null
  let abortLock: AbortController | null = null
  let ringing = false
  let ringingId = ''
  let notice: Notification | null = null
  let baseTitle = ''
  let ringTitle = ''

  const permission = ref<NotificationPermission>(
    notificationsSupported() ? Notification.permission : 'denied',
  )

  function closeAudio() {
    void audio?.close().catch(() => {})
    audio = null
  }

  /** Играет мелодию; false — браузер держит звук на паузе (на странице ещё не кликали). */
  async function playTone(): Promise<boolean> {
    let ctx: AudioContext | null = null
    try {
      ctx = new AudioContext()
      audio = ctx
      const gain = ctx.createGain()
      gain.gain.value = 0
      gain.connect(ctx.destination)
      for (const hz of [425, 480]) {
        const osc = ctx.createOscillator()
        osc.frequency.value = hz
        osc.connect(gain)
        osc.start()
      }
      const t0 = ctx.currentTime
      for (let cycle = 0; cycle * RING_PERIOD_S < RING_TOTAL_S; cycle++) {
        for (const [on, off] of RING_BURSTS) {
          gain.gain.setValueAtTime(RING_GAIN, t0 + cycle * RING_PERIOD_S + on)
          gain.gain.setValueAtTime(0, t0 + cycle * RING_PERIOD_S + off)
        }
      }
      const wait =
        navigator.userActivation?.hasBeenActive === false
          ? RESUME_WAIT_NO_GESTURE_MS
          : RESUME_WAIT_MS
      await Promise.race([
        ctx.resume().catch(() => {}),
        new Promise((resolve) => setTimeout(resolve, wait)),
      ])
      if (ctx.state === 'running') return true
    } catch {
      // Web Audio нет — остаются баннер, уведомление и заголовок вкладки.
    }
    // Закрываем свой контекст: за время ожидания мог начаться следующий звонок.
    void ctx?.close().catch(() => {})
    if (audio === ctx) audio = null
    return false
  }

  function startRing() {
    ringing = true
    if (!navigator.locks) {
      void playTone()
      return
    }
    // Замок ждём в очереди: вкладка, которая не может звучать или закрылась посреди
    // звонка, уступает его следующей.
    const abort = new AbortController()
    abortLock = abort
    navigator.locks
      .request(RING_LOCK, { signal: abort.signal }, async () => {
        if (!ringing || abortLock !== abort) return
        if (!(await playTone())) return
        if (!ringing || abortLock !== abort) {
          closeAudio()
          return
        }
        return new Promise<void>((resolve) => {
          releaseLock = resolve
        })
      })
      .catch(() => {
        // AbortError — звонок кончился, пока ждали замок.
      })
  }

  /** now — замок отпустить сразу: звонит дальше эта же вкладка или она закрывается. */
  function stopRing(now = false) {
    ringing = false
    abortLock?.abort()
    abortLock = null
    closeAudio()
    const release = releaseLock
    releaseLock = null
    if (release && now) release()
    else if (release) setTimeout(release, RELEASE_DELAY_MS)
  }

  function notify(a: IncomingCallAlert) {
    if (permission.value !== 'granted' || document.hasFocus()) return
    try {
      notice?.close()
      const n = new Notification(a.title, {
        body: a.body,
        tag: CALL_NOTIFICATION_TAG,
        icon: '/icon-192.png',
        requireInteraction: true,
      })
      n.onclick = () => {
        window.focus()
        n.close()
      }
      notice = n
    } catch {
      // Android Chrome показывает уведомления только через service worker — там придёт web push.
      notice = null
    }
  }

  function closeNotices() {
    notice?.close()
    notice = null
    // И web push о звонке, если он пришёл в этот же браузер.
    void navigator.serviceWorker
      ?.getRegistration()
      .then((reg) => reg?.getNotifications({ tag: CALL_NOTIFICATION_TAG }))
      .then((list) => list?.forEach((n) => n.close()))
      .catch(() => {})
  }

  function setTitle(a: IncomingCallAlert) {
    if (!ringTitle) baseTitle = document.title
    document.title = `📞 ${a.body} · ${baseTitle}`
    // Браузер схлопывает пробелы в заголовке: сравнивать будем с тем, что он хранит.
    ringTitle = document.title
  }

  function restoreTitle() {
    // Пока звонило, заголовок могли сменить — чужой не перетираем.
    if (ringTitle && document.title === ringTitle) document.title = baseTitle
    ringTitle = ''
  }

  function stop(now = false) {
    stopRing(now)
    ringingId = ''
    closeNotices()
    restoreTitle()
  }

  watch(
    alert,
    (a) => {
      if (!a) {
        stop()
        return
      }
      // Следующий звонок из очереди — мелодия заново: расписание у прежнего кончится.
      if (a.id !== ringingId) {
        stopRing(true)
        startRing()
        ringingId = a.id
      }
      notify(a)
      setTitle(a)
    },
    { immediate: true },
  )

  onBeforeUnmount(() => stop(true))

  /** Разрешение спрашиваем по клику: без жеста браузеры прячут запрос. */
  async function askPermission() {
    if (!notificationsSupported()) return
    // Свёрнутая CRM на iPhone (DEV-2685) покажет звонок только web push: страница там
    // уведомлений не создаёт. Разрешение подписка спрашивает сама, первым делом в клике.
    const subscribed = await useWebPush().subscribe()
    if (!subscribed && Notification.permission === 'default') await Notification.requestPermission()
    // Старый Safari отвечает undefined — читаем само разрешение.
    permission.value = Notification.permission
    if (permission.value === 'granted' && alert.value) notify(alert.value)
  }

  return { permission, askPermission }
}
